/**
 * KeyPersonsCard - 关键人卡片组件
 * 
 * 在商机概览页面右侧栏展示关键人信息
 * 包括：名称、KB角色、立场
 */
import { Users, ChevronRight } from 'lucide-react'
import { clsx } from 'clsx'
import type { Person, PersonRole } from '@/types'

interface KeyPersonsCardProps {
  persons: Person[]
  isLoading?: boolean
  maxDisplay?: number
  onViewAll?: () => void
}

// 角色优先级排序（EB > TB > Coach > UB > Other）
const ROLE_PRIORITY: Record<PersonRole, number> = {
  EB: 5,
  TB: 4,
  Coach: 3,
  UB: 2,
  Other: 1,
}

// 角色显示配置
const ROLE_CONFIG: Record<PersonRole, { label: string; color: string; bgColor: string }> = {
  EB: { label: 'EB', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  TB: { label: 'TB', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  Coach: { label: 'Coach', color: 'text-green-700', bgColor: 'bg-green-100' },
  UB: { label: 'UB', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  Other: { label: 'Other', color: 'text-gray-600', bgColor: 'bg-gray-100' },
}

// 立场显示配置
const ATTITUDE_CONFIG: Record<string, { label: string; color: string }> = {
  '支持': { label: '支持', color: 'text-green-600' },
  'Support': { label: '支持', color: 'text-green-600' },
  'support': { label: '支持', color: 'text-green-600' },
  '反对': { label: '反对', color: 'text-red-600' },
  'Oppose': { label: '反对', color: 'text-red-600' },
  'oppose': { label: '反对', color: 'text-red-600' },
  '中立': { label: '中立', color: 'text-gray-500' },
  'Neutral': { label: '中立', color: 'text-gray-500' },
  'neutral': { label: '中立', color: 'text-gray-500' },
  '未知': { label: '未知', color: 'text-gray-400' },
  'Unknown': { label: '未知', color: 'text-gray-400' },
  'unknown': { label: '未知', color: 'text-gray-400' },
}

function getAttitudeConfig(attitude: string | undefined) {
  if (!attitude) return ATTITUDE_CONFIG['未知']
  return ATTITUDE_CONFIG[attitude] || ATTITUDE_CONFIG['未知']
}

function getRoleConfig(role: PersonRole | undefined) {
  if (!role) return ROLE_CONFIG['Other']
  return ROLE_CONFIG[role] || ROLE_CONFIG['Other']
}

export default function KeyPersonsCard({
  persons,
  isLoading = false,
  maxDisplay = 5,
  onViewAll,
}: KeyPersonsCardProps) {
  // 按角色优先级排序
  const sortedPersons = [...persons].sort((a, b) => {
    const priorityA = ROLE_PRIORITY[a.primaryRole] || 0
    const priorityB = ROLE_PRIORITY[b.primaryRole] || 0
    return priorityB - priorityA
  })

  const displayPersons = sortedPersons.slice(0, maxDisplay)
  const hasMore = sortedPersons.length > maxDisplay

  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-xl border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-500">关键人</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-1">
                <div className="h-4 bg-gray-200 rounded w-20" />
                <div className="h-3 bg-gray-100 rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (persons.length === 0) {
    return (
      <div className="p-4 bg-white rounded-xl border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-500">关键人</h3>
        </div>
        <p className="text-sm text-gray-400 text-center py-4">
          暂无关键人信息
        </p>
        <p className="text-xs text-gray-400 text-center">
          添加证据后自动识别关键人
        </p>
      </div>
    )
  }

  return (
    <div className="p-4 bg-white rounded-xl border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-500">关键人</h3>
          <span className="text-xs text-gray-400">({persons.length})</span>
        </div>
      </div>

      <div className="space-y-3">
        {displayPersons.map((person) => {
          const roleConfig = getRoleConfig(person.primaryRole)
          const attitudeConfig = getAttitudeConfig(person.attitude)

          return (
            <div
              key={person.id}
              className="flex items-center gap-3 group"
            >
              {/* 头像 */}
              <div className={clsx(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                roleConfig.bgColor,
                roleConfig.color
              )}>
                {person.name.charAt(0)}
              </div>

              {/* 信息 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">
                    {person.name}
                  </span>
                  {person.isConfirmedEb && (
                    <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                      已确认
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded',
                    roleConfig.bgColor,
                    roleConfig.color
                  )}>
                    {roleConfig.label}
                  </span>
                  <span className={attitudeConfig.color}>
                    {attitudeConfig.label}
                  </span>
                </div>
                {/* 职务和部门信息 */}
                {(person.title || person.department) && (
                  <div className="text-xs text-gray-400 truncate mt-0.5" title={[person.title, person.department].filter(Boolean).join(' · ')}>
                    {[person.title, person.department].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* 查看更多 */}
      {hasMore && onViewAll && (
        <button
          onClick={onViewAll}
          className="w-full mt-3 pt-3 border-t border-gray-100 flex items-center justify-center gap-1 text-xs text-primary-600 hover:text-primary-700"
        >
          查看全部 {persons.length} 人
          <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  )
}
